import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { MaskPipe } from 'ngx-mask';

type ProviderData = {
  id?: string | number;
  name: string;
  document?: string;
  provider_type: string;
  role: string;
};

@Component({
  selector: 'app-dashboard-approval-providers',
  templateUrl: './dashboard-approval-providers.component.html',
  styleUrls: ['./dashboard-approval-providers.component.scss'],
})
export class DashboardApprovalProvidersComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() comparingVersions: boolean = false;

  data: ProviderData[] = [];
  loading = true;
  role: string = '';
  canView = false;

  // Dados para versão principal
  mainData: ProviderData[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingData: ProviderData[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  eventMainVersion: any;
  eventComparingVersion: any;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private api: ApiService,
    private router: Router,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef,
    private maskPipe: MaskPipe
  ) {}

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    this.canView = this.authService.verifyPermission(
      `de_can_view_${this.router.url.split('/')[range - 2]}_providers_details`
    );

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.service_providers) {
        this.data = [...this.approvalService.approvalData.service_providers].reverse();
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange
    ) {
      const previousValue = changes['comparingVersions'].previousValue;
      const currentValue = changes['comparingVersions'].currentValue;

      // Se mudou de true para false, recarregar dados
      if (previousValue === true && currentValue === false) {
        if (this.approvalService.approvalData.service_providers) {
          this.data = [...this.approvalService.approvalData.service_providers].reverse();
          this.loading = false;
        } else {
          this.getApprovalData();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.eventMainVersion?.unsubscribe();
    this.eventComparingVersion?.unsubscribe();
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainRevision) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingRevision) {
      this.loadComparingVersion();
    }
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    try {
      this.comparingDataLoading = true;
      this.cdr.detectChanges();

      if (!this.mainData || !this.comparingData) {
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        return;
      }

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem) => {
        const referenceItem = this.mainData.find(
          (ref) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const changedFields = {
            name: comparingItem.name !== referenceItem.name,
            document: comparingItem.document !== referenceItem.document,
            provider_type:
              comparingItem.provider_type !== referenceItem.provider_type,
            role: comparingItem.role !== referenceItem.role,
          };

          const hasChanges = Object.values(changedFields).some(
            (changed) => changed
          );

          // Adicionar campos formatados para exibição
          const formattedItem = {
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          };

          processedData.push(formattedItem);
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem) => {
        const existsInComparing = this.comparingData.find(
          (comp) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingData = processedData;

      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainRevision) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingRevision) {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'service_providers',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainRevision) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingRevision))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      const admin =
        data[0].register.admin && data[0].register.admin.length > 0
          ? data[0].register.admin[0]
          : null;
      const manager =
        data[0].register.manager && data[0].register.manager.length > 0
          ? data[0].register.manager[0]
          : null;

      if (admin) {
        admin.provider_type = 'Administrador';
      }

      if (manager) {
        manager.provider_type = 'Gestor';
      }

      const providersData = [admin, manager].filter((item) => item);

      if (versionType === 'main') {
        this.mainData = providersData.reverse();
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.comparingVersions && this.comparingData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingData = providersData.reverse();
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.comparingVersions && this.mainData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
      } else {
        const reversedData = [...providersData].reverse();
        this.approvalService.approvalData.service_providers = reversedData;
        this.data = reversedData;
        this.loading = false;
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.cdr.detectChanges();
      } else {
        this.loading = false;
      }
    }
  }

  formatDocument(document: string) {
    if (!document) return '-';
    if (document.length < 14) {
      return (
        'CPF: ' +
        document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
      );
    }

    return (
      'CNPJ: ' +
      document.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    );
  }

  formatCpf(value: string) {
    if (!value) return '-';
    const newValue = this.maskPipe.transform(value, '000.000.000-00');
    return newValue;
  }

  getInitials(name: string) {
    if (!name) return '';
    return name
      .toUpperCase()
      .replace(/[^\w\s]/gi, '')
      .split(' ')
      .slice(0, 2)
      .map((word: string) => word.charAt(0))
      .join('');
  }

  openModalById(id: string | number) {
    try {
      let allData: any[] = [];

      if (this.comparingVersions) {
        allData = [...this.mainData, ...this.comparingData];
      } else {
        allData = this.data;
      }

      const finded = allData.find((item: any) => {
        if (item.id) {
          return String(item.id) === String(id);
        }
        return false;
      });

      if (finded && this.canView) {
        window.location.href = `/app/details/provider/${finded.id}`;
      }
    } catch (error) {
      // Erro silencioso
    }
  }
}
